// Iter 99 — ONE-SURFACE RULE (Howard ruling): for LP estimates the
// printable Material List composes from the DERIVED PACKAGE — the exact
// payload the Material List tab renders (colors + session substitutions
// included) — never from stored legacy lines.
// ONE MONEY SURFACE (ruled 2026-07-23, P0 double-count on a live customer
// quote): the printed list is UNPRICED — items, quantities, units,
// derivations, provenance only. Pricing lives on the estimate tabs.
const FONT = "'Helvetica Neue', Helvetica, Arial, sans-serif";
const C = {
  ink: "#09090B", muted: "#52525B", faint: "#71717A",
  line: "#D4D4D8", accent: "#F97316", accentText: "#C2410C", bg: "#FAFAFA",
};

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export function buildLpMaterialListHtml({ pkg, estimate, company, branding, lang = "en", share = null, trail = null }) {
  const es = lang === "es";
  const bySection = {};
  (pkg.lines || []).forEach((l) => {
    (bySection[l.section] = bySection[l.section] || []).push(l);
  });
  const groupColors = pkg.summary?.group_colors || {};
  const colorLine = Object.entries(groupColors)
    .filter(([, v]) => v)
    .map(([g, v]) => `${g.replace("_", " ")}: ${v}`)
    .join(" · ");

  const sectionBlock = ([section, lines]) => `
    <tr><td colspan="4" style="padding:10px 8px 4px;font-size:10px;font-weight:700;
      letter-spacing:0.08em;text-transform:uppercase;color:${C.accentText};
      border-bottom:1px solid ${C.line};">${esc(section)}</td></tr>
    ${lines
      .map((l) => {
        const sub = l.substituted_from
          ? `<div style="font-size:8.5px;color:${C.faint};">${es ? "sustituido de" : "substituted from"} ${esc(l.substituted_from)} — re-derived</div>`
          : "";
        const colorFlags = (l.color_flags || [])
          .map((f) => `<div style="font-size:8px;font-weight:700;color:${l.color_status === "unsupported" ? "#B91C1C" : "#B45309"};">⚑ ${esc(f)}</div>`)
          .join("");
        return `
      <tr style="border-bottom:1px solid ${C.line};">
        <td style="padding:6px 8px;font-size:10px;color:${C.ink};">${esc(l.name)}${sub}${colorFlags}</td>
        <td style="padding:6px 8px;font-size:9.5px;color:${C.muted};">${esc(l.color || "—")}</td>
        <td style="padding:6px 8px;font-size:10px;text-align:right;">${esc(l.qty)}</td>
        <td style="padding:6px 8px;font-size:9.5px;color:${C.muted};">${esc(l.unit)}</td>
      </tr>`;
      })
      .join("")}`;

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
    @page { size: Letter; margin: 14mm 12mm; }
    body { font-family:${FONT}; color:${C.ink}; margin:0; }
  </style></head><body>
    <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid ${C.accent};padding-bottom:10px;">
      <div>
        <div style="font-size:16px;font-weight:800;letter-spacing:0.04em;">${es ? "LISTA DE MATERIALES" : "MATERIAL LIST"} — LP SMARTSIDE</div>
        <div style="font-size:10px;color:${C.muted};margin-top:2px;">
          ${esc(company?.name || branding?.supplier_name || "")}</div>
      </div>
      <div style="text-align:right;font-size:10px;color:${C.muted};">
        <div style="font-weight:700;color:${C.ink};">${esc(estimate?.estimate_number || "")}</div>
        <div>${esc(estimate?.customer_name || "")}</div>
        <div>${esc(estimate?.address || "")}</div>
        <div>${esc(estimate?.estimate_date || "")}</div>
      </div>
    </div>
    <div style="font-size:8.5px;color:${C.faint};margin:6px 0 10px;">
      ${es ? "Derivada de mediciones IA confirmadas" : "Derived from confirmed AI measurements"}
      — run ${esc(String(pkg.run_id || "").slice(0, 8))} · ${es ? "fuente única" : "single source"}
      ${colorLine ? ` · ${esc(colorLine)}` : ""}
    </div>
    <table style="width:100%;border-collapse:collapse;">
      <thead><tr style="background:${C.bg};border-bottom:2px solid ${C.ink};">
        ${[es ? "Artículo" : "Item", "Color", es ? "Cant." : "Qty", es ? "Unidad" : "Unit"]
          .map((h, i) => `<th style="padding:6px 8px;font-size:9px;letter-spacing:0.08em;text-transform:uppercase;color:${C.muted};text-align:${i === 2 ? "right" : "left"};">${h}</th>`)
          .join("")}
      </tr></thead>
      <tbody>${Object.entries(bySection).map(sectionBlock).join("")}</tbody>
    </table>
    <div style="display:flex;justify-content:space-between;gap:24px;margin-top:12px;padding-top:8px;border-top:2px solid ${C.ink};">
      <div style="font-size:8.5px;color:${C.faint};align-self:center;">${es
        ? "Lista de materiales — cantidades y derivaciones. Los precios se proporcionan en el estimado."
        : "Material list — quantities & derivations. Pricing is provided on the estimate itself."}</div>
    </div>
    ${trail ? `
    <div style="margin-top:10px;padding:6px 8px;border:1px solid ${C.line};font-size:8.5px;color:${C.muted};">
      <span style="font-weight:700;text-transform:uppercase;letter-spacing:0.08em;font-size:8px;">${es ? "Rastro de verificación" : "Verification trail"}</span>
      · ${trail.amber.total === 0
        ? (es ? "sin ubicaciones ámbar en esta corrida" : "no amber locations flagged on this run")
        : `${es ? "ámbar" : "amber"}: ${trail.amber.verified}/${trail.amber.total} ${es ? "verificadas en campo" : "field-verified"}${
            trail.amber.verified > 0 && trail.amber.verifiers.length
              ? ` (${esc(trail.amber.verifiers.join(", "))})` : ""}${
            trail.amber.unverified.length
              ? ` — <b style="color:#B45309;">${es ? "SIN VERIFICAR" : "UNVERIFIED"}: ${esc(trail.amber.unverified.join("; "))}</b>` : ""}`}
      ${trail.openings
        ? ` · ${es ? "aberturas" : "openings"}: ${trail.openings.confirmed + trail.openings.corrected}/${trail.openings.total} ${es ? "revisadas" : "reviewed"}${
            trail.openings.corrected ? ` (${trail.openings.corrected} ${es ? "corregidas" : "corrected"})` : ""}${
            trail.openings.unconfirmed ? ` — <b style="color:#B45309;">${trail.openings.unconfirmed} ${es ? "SIN CONFIRMAR" : "UNCONFIRMED"}</b>` : ""}`
        : ""}
    </div>` : ""}
    ${share ? `
    <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;margin-top:16px;padding-top:10px;border-top:1px solid ${C.line};">
      <div style="font-size:8.5px;color:${C.faint};">
        <div style="font-weight:700;color:${C.muted};text-transform:uppercase;letter-spacing:0.08em;font-size:8px;">${es ? "Copia digital de ESTA lista impresa" : "Digital copy of THIS printed list"}</div>
        <div style="margin-top:2px;max-width:420px;">${es
          ? "El código QR abre exactamente la versión congelada aquí impresa — si el estimado cambia después, la página avisará que existe una lista más reciente."
          : "The QR opens the exact frozen version printed here — if the estimate changes later, the page will flag that a newer list exists."}</div>
        <div style="margin-top:2px;">${esc(share.shareUrl)} · ${es ? "impreso" : "printed"} ${esc(new Date(share.printedAt).toLocaleDateString(es ? "es" : "en-US"))}</div>
      </div>
      <img src="${share.qrDataUrl}" width="76" height="76" style="width:76px;height:76px;flex:none;" />
    </div>` : ""}
  </body></html>`;
}
